import {
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  Inject,
  inject,
  Injector,
  Input,
  OnInit,
  Output
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALIDATORS,
  ReactiveFormsModule,
  Validator,
  Validators
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GridColumnType } from 'src/models/ts/grid-column-type.model';
import { TranslatePipe } from 'src/app/shared/pipes/translate/translate.pipe';
import { ComboBoxModule, MultiSelectModule } from '@progress/kendo-angular-dropdowns';
import { UpdateDeleteState } from 'src/models/ts/update-delete-state.model';
import { DepartmentType } from 'src/models/ts/department-type';
import { OrganizationChartItemType } from 'src/models/ts/organization-chart-item-type.model';
import { IconComponent } from 'src/app/shared/components/ui/icon/icon.component';
import { ColorPickerComponent } from '@progress/kendo-angular-inputs';
import { ColorService } from 'src/app/core/services/colors/color.service';
import { BizzColorPaletteSettings } from 'src/app/shared/constants/color-palette-settings';
import { CHECKLIST_BUTTON_DEFAULT_COLORS } from 'src/app/features/bizzmine/form/constants/checklist-button-default-colors';
import { ResultClassValue } from 'src/models/ts/result-class-value';
import { distinctUntilChanged } from 'rxjs';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { SCORE_FIELD } from '../../result-lists-checklist-control.component';

@Component({
  selector: 'bizz-checklist-edit-cell',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    ComboBoxModule,
    MultiSelectModule,
    IconComponent,
    ColorPickerComponent
  ],
  templateUrl: './checklist-edit-cell.component.html',
  styleUrl: './checklist-edit-cell.component.scss'
})
export class ChecklistEditCellComponent implements OnInit {
  public colorService = inject(ColorService);
  public themeService = inject(ThemeService);
  @Input() public formGroup: FormGroup;
  @Input() public id: number;
  @Input() public data: Array<any>;
  @Input() public column: {
    field: string,
    GridColumnType: GridColumnType,
    Enum: Array<{ Value: any | null, Text: string }>,
    required: boolean,
  };
  @Input() public value: any;
  @Input() public canShowNoneOption: boolean = true;
  @Output() public valueChange = new EventEmitter<any>();

  public isDarkTheme = computed(() => this.themeService.currentTheme$.value?.IsDarkTheme);

  public get rowData(): ResultClassValue {
    return this.data?.find(row => row.ID === this.id) as ResultClassValue;
  }

  public readonly GridColumnType = GridColumnType;
  public readonly BizzColorPaletteSettings = BizzColorPaletteSettings;
  public control: FormControl;
  public selectedColor: Array<{ Value: any | null, Text: string }> = [];
  protected readonly DepartmentType = DepartmentType;
  protected readonly OrganizationChartItemType = OrganizationChartItemType;
  private destroyRef = inject(DestroyRef);

  protected CHECKLIST_BUTTON_DEFAULT_COLORS = CHECKLIST_BUTTON_DEFAULT_COLORS;

  public selectedColorPresetChanged(value: any): void {
    if (value.length > 0 && value[value.length - 1]?.Value) {
      const newVal = value[value.length - 1];
      this.selectedColor = [newVal];
      this.setForegroundColor(newVal.Value.BackgroundColor);
      this.control.setValue(newVal.Value.BackgroundColor);
    } else if (this.canShowNoneOption) {
      this.selectedColor = [];
      this.control.setValue(null);
      //workaround set old value to prevent clearing it.
    } else if (this.value) {
      const preset = this.getColorPreset(this.value);
      if (preset)
        this.selectedColor = [preset];
      else this.customColorEnabled(this.value);
    }
  }

  public constructor(@Inject(Injector) public injector: Injector){}

  public setForegroundColor(bgColor: string): string {
    if (bgColor != null && bgColor.length >= 7) {
      const preset = this.getColorPreset(bgColor);
      if (preset == null || preset.Text == 'Custom')
        this.rowData.ForegroundColor = this.colorService.generate800ContrastColor(bgColor);
      else if (preset.Value)
        this.rowData.ForegroundColor = preset.Value.ForegroundColor;
    }
    return this.rowData.ForegroundColor ?? '#000000';
  }

  public ngOnInit(): void {
    if (!this.canShowNoneOption)
      this.CHECKLIST_BUTTON_DEFAULT_COLORS = this.CHECKLIST_BUTTON_DEFAULT_COLORS.filter(e => e.Text != 'None');
    this.control = this.formGroup?.get(this.id.toString())?.get(this.column.field) as FormControl;
    if (this.column.field === SCORE_FIELD){
      this.control.setValidators([Validators.min(0), Validators.max(100)])
      this.control.updateValueAndValidity()
    }

    this.control.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      distinctUntilChanged())
      .subscribe(value => {
        if (this.column.GridColumnType == GridColumnType.ColorEnum) {
          if (value)
            this.setForegroundColor(value);
          this.customColorEnabled(value);
        }

        if (this.rowData != undefined && this.rowData.UpdateDeleteState == UpdateDeleteState.Default) {
          this.rowData.UpdateDeleteState = UpdateDeleteState.Update;
        }
        this.valueChange.emit(value);
      });

    if (this.column.GridColumnType == GridColumnType.ColorEnum ||
      this.column.GridColumnType == GridColumnType.CustomColorEnum) {
      this.setForegroundColor(this.control.value);
    }
    this.customColorEnabled(this.control.value);
  }

  public customColorEnabled(colorValue: any): void {
    if (this.column.GridColumnType == GridColumnType.ColorEnum) {
      const colorPreset = this.CHECKLIST_BUTTON_DEFAULT_COLORS.find(e =>
        e.Value?.BackgroundColor == colorValue);
      if (colorPreset != null) {
        this.selectedColor = [colorPreset];
      } else {
        const customColorPreset = structuredClone(CHECKLIST_BUTTON_DEFAULT_COLORS.find(e => e.Text == 'Custom'));
        if (customColorPreset == null || customColorPreset.Value == null) {
          console.error('Custom color preset not found');
          return;
        }
        customColorPreset.Value.BackgroundColor = colorValue;
        this.selectedColor = [customColorPreset];
      }
    }
  }

  public getColorPreset(color: string): {
    Value: { BackgroundColor: string, ForegroundColor: string } | null,
    Text: string
  } | undefined {
    return CHECKLIST_BUTTON_DEFAULT_COLORS.find(e => e.Value?.BackgroundColor == color);
  }

  public onCustomColorChanged(color: string): void {
    if (this.control.value != color) {
      this.setForegroundColor(color);
      this.control.setValue(color);
    }
  }

  public onValueChange(value: any): void {
    this.control.setValue(value);
  }



  protected readonly SCORE_FIELD = SCORE_FIELD;
}
